import * as React from "react";
import { cn } from "@/lib/utils";

export type StatusTone = "brand" | "success" | "warning" | "neutral" | "purple" | "error";

const toneClass: Record<StatusTone, string> = {
  brand: "border-brand-200 bg-brand-50 text-brand-600",
  success: "border-success-200 bg-success-50 text-success-700",
  warning: "border-warning-300 bg-warning-50 text-warning-600",
  neutral: "border-gray-300 bg-gray-100 text-gray-700",
  purple: "border-purple-200 bg-purple-50 text-purple-600",
  error: "border-error-100 bg-error-50 text-error-600",
};

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: StatusTone;
}

/** Uppercase status pill (units / projects) per Design System v2026. */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  tone = "neutral",
  className,
  children,
  ...rest
}) => (
  <span
    className={cn(
      "inline-flex items-center rounded-md border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.04em]",
      toneClass[tone],
      className,
    )}
    {...rest}
  >
    {children}
  </span>
);

/** Maps a unit sale/rent status key to a tone (Figma color map). */
export const unitStatusTone = (status?: string): StatusTone => {
  switch ((status || "").toLowerCase()) {
    case "kapora":
    case "kapora_alindi":
    case "reserved":
      return "brand";
    case "satildi":
    case "sold":
      return "success";
    case "satiliyor":
    case "selling":
    case "for_sale":
      return "warning";
    case "kiralandi":
    case "rented":
      return "brand";
    case "kiralik":
    case "for_rent":
      return "purple";
    case "bos":
    case "empty":
    case "bekliyor":
    case "pending":
      return "neutral";
    default:
      return "neutral";
  }
};

/** Maps a project status to a tone. */
export const projectStatusTone = (status?: string): StatusTone => {
  switch ((status || "").toLowerCase()) {
    case "completed":
      return "success";
    case "delayed":
    case "stopped":
      return "neutral";
    default:
      return "brand";
  }
};

export default StatusBadge;
